import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './ThankYouPage.css';
import api from '../../api/axios';
import { API_IMAGE_BASE_URL } from '../../config/api';

const PAGE_TYPES = [
  { type: 'registration', label: 'Registration Success', icon: '🎉' },
  { type: 'payment', label: 'Payment Success', icon: '💳' },
];

const ICON_OPTIONS = [
  { value: 'check', label: '✓ Checkmark' },
  { value: 'celebration', label: '🎉 Celebration' },
  { value: 'payment', label: '💳 Payment' },
  { value: 'heart', label: '❤️ Heart' },
  { value: 'custom', label: '🖼 Custom Image' },
];

const EMPTY_FORM = {
  pageType: 'registration',
  pageLabel: '',
  headline: '',
  subtitle: '',
  description: '',
  heroImage: '',
  backgroundColor: '#f5f0e8',
  accentColor: '#e9af66',
  textColor: '#3f3f3f',
  iconType: 'check',
  customIconUrl: '',
  primaryButtonText: 'Continue',
  primaryButtonLink: '',
  secondaryButtonText: '',
  secondaryButtonLink: '',
  showConfetti: true,
  extraContent: '',
  isActive: true,
};

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  return `${API_IMAGE_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

const WEBSITE_BASE_URL =
  process.env.REACT_APP_WEBSITE_URL || 'https://bandhanguru.com';

const ThankYouPageManagement = () => {
  const [pages, setPages] = useState([]);
  const [activeTab, setActiveTab] = useState('registration');
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [uploadingField, setUploadingField] = useState(null);
  const heroFileRef = useRef(null);
  const iconFileRef = useRef(null);

  const fetchPages = async () => {
    try {
      setFetching(true);
      const res = await api.get('/admin/thank-you-page');
      setPages(res.data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => { fetchPages(); }, []);

  useEffect(() => {
    const current = pages.find((p) => p.pageType === activeTab);
    if (current) {
      setForm({
        pageType: current.pageType,
        pageLabel: current.pageLabel || '',
        headline: current.headline || '',
        subtitle: current.subtitle || '',
        description: current.description || '',
        heroImage: current.heroImage || '',
        backgroundColor: current.backgroundColor || '#f5f0e8',
        accentColor: current.accentColor || '#e9af66',
        textColor: current.textColor || '#3f3f3f',
        iconType: current.iconType || 'check',
        customIconUrl: current.customIconUrl || '',
        primaryButtonText: current.primaryButtonText || 'Continue',
        primaryButtonLink: current.primaryButtonLink || '',
        secondaryButtonText: current.secondaryButtonText || '',
        secondaryButtonLink: current.secondaryButtonLink || '',
        showConfetti: current.showConfetti !== false,
        extraContent: current.extraContent || '',
        isActive: current.isActive !== false,
      });
      setEditingId(current._id || null);
    } else {
      setForm({ ...EMPTY_FORM, pageType: activeTab });
      setEditingId(null);
    }
  }, [activeTab, pages]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const uploadImage = async (file, field) => {
    if (!file) return;
    setUploadingField(field);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await api.post('/admin/thank-you-page/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const imageUrl = res.data?.data?.imageUrl;
      if (imageUrl) {
        handleChange(field, imageUrl);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploadingField(null);
    }
  };

  const handleImageSelect = (e, field) => {
    const file = e.target.files?.[0];
    if (file) uploadImage(file, field);
    e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.headline.trim()) {
      alert('Headline is required');
      return;
    }
    setLoading(true);
    try {
      await api.put('/admin/thank-you-page', { ...form, pageType: activeTab });
      await fetchPages();
      alert('Thank you page saved successfully!');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save thank you page');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!editingId) return;
    if (!window.confirm('Reset this page to default content? Custom settings will be removed.')) return;
    try {
      await api.delete(`/admin/thank-you-page/${editingId}`);
      await fetchPages();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to reset page');
    }
  };

  const renderPreviewIcon = () => {
    if (form.iconType === 'custom' && form.customIconUrl) {
      return <img src={resolveImageUrl(form.customIconUrl)} alt="" className="preview-custom-icon" />;
    }
    const icons = { check: '✓', celebration: '🎉', payment: '💳', heart: '❤️' };
    return <span className="preview-icon-emoji">{icons[form.iconType] || '✓'}</span>;
  };

  return (
    <div className="thankyou-management">
      <div className="page-header">
        <div>
          <h1>Thank You Pages</h1>
          <p>Design and control success pages shown after registration and payment</p>
        </div>
        <a
          href={`${WEBSITE_BASE_URL}/thank-you/${activeTab}`}
          target="_blank"
          rel="noopener noreferrer"
          className="preview-live-link"
        >
          Preview on Website ↗
        </a>
      </div>

      <div className="thankyou-tabs">
        {PAGE_TYPES.map((tab) => (
          <button
            key={tab.type}
            type="button"
            className={`thankyou-tab ${activeTab === tab.type ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.type)}
          >
            <span>{tab.icon}</span> {tab.label}
          </button>
        ))}
      </div>

      {fetching ? (
        <div className="thankyou-loading">Loading...</div>
      ) : (
        <div className="thankyou-layout">
          <form className="thankyou-form" onSubmit={handleSubmit}>
            <div className="form-section">
              <h3>Page Content</h3>
              <div className="form-group">
                <label>Page Label</label>
                <input
                  value={form.pageLabel}
                  onChange={(e) => handleChange('pageLabel', e.target.value)}
                  placeholder="Internal label for admin reference"
                />
              </div>
              <div className="form-group">
                <label>Headline *</label>
                <input
                  value={form.headline}
                  onChange={(e) => handleChange('headline', e.target.value)}
                  placeholder="Main title shown on the page"
                />
              </div>
              <div className="form-group">
                <label>Subtitle</label>
                <input
                  value={form.subtitle}
                  onChange={(e) => handleChange('subtitle', e.target.value)}
                  placeholder="Secondary line below headline"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <ReactQuill
                  theme="snow"
                  value={form.description}
                  onChange={(val) => handleChange('description', val)}
                  modules={quillModules}
                />
              </div>
              <div className="form-group">
                <label>Extra Content Block</label>
                <ReactQuill
                  theme="snow"
                  value={form.extraContent}
                  onChange={(val) => handleChange('extraContent', val)}
                  modules={quillModules}
                />
              </div>
            </div>

            <div className="form-section">
              <h3>Design & Colors</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Background Color</label>
                  <div className="color-input-wrap">
                    <input type="color" value={form.backgroundColor} onChange={(e) => handleChange('backgroundColor', e.target.value)} />
                    <input value={form.backgroundColor} onChange={(e) => handleChange('backgroundColor', e.target.value)} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Accent Color</label>
                  <div className="color-input-wrap">
                    <input type="color" value={form.accentColor} onChange={(e) => handleChange('accentColor', e.target.value)} />
                    <input value={form.accentColor} onChange={(e) => handleChange('accentColor', e.target.value)} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Text Color</label>
                  <div className="color-input-wrap">
                    <input type="color" value={form.textColor} onChange={(e) => handleChange('textColor', e.target.value)} />
                    <input value={form.textColor} onChange={(e) => handleChange('textColor', e.target.value)} />
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label>Hero Image</label>
                <input
                  value={form.heroImage}
                  onChange={(e) => handleChange('heroImage', e.target.value)}
                  placeholder="Optional banner/hero image URL"
                />
                <input
                  type="file"
                  accept="image/*"
                  ref={heroFileRef}
                  onChange={(e) => handleImageSelect(e, 'heroImage')}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  className="upload-btn"
                  onClick={() => heroFileRef.current?.click()}
                  disabled={uploadingField === 'heroImage'}
                >
                  {uploadingField === 'heroImage' ? 'Uploading...' : 'Upload Hero Image'}
                </button>
                {form.heroImage && (
                  <div className="image-preview-wrap">
                    <img src={resolveImageUrl(form.heroImage)} alt="Hero preview" />
                  </div>
                )}
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Success Icon</label>
                  <select value={form.iconType} onChange={(e) => handleChange('iconType', e.target.value)}>
                    {ICON_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                {form.iconType === 'custom' && (
                  <div className="form-group">
                    <label>Custom Icon</label>
                    <input
                      value={form.customIconUrl}
                      onChange={(e) => handleChange('customIconUrl', e.target.value)}
                      placeholder="Custom icon image URL"
                    />
                    <input
                      type="file"
                      accept="image/*"
                      ref={iconFileRef}
                      onChange={(e) => handleImageSelect(e, 'customIconUrl')}
                      style={{ display: 'none' }}
                    />
                    <button
                      type="button"
                      className="upload-btn"
                      onClick={() => iconFileRef.current?.click()}
                      disabled={uploadingField === 'customIconUrl'}
                    >
                      {uploadingField === 'customIconUrl' ? 'Uploading...' : 'Upload Custom Icon'}
                    </button>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={form.showConfetti}
                    onChange={(e) => handleChange('showConfetti', e.target.checked)}
                  />
                  &nbsp; Show confetti animation
                </label>
              </div>
              <div className="form-group">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => handleChange('isActive', e.target.checked)}
                  />
                  &nbsp; Active (show on website)
                </label>
              </div>
            </div>

            <div className="form-section">
              <h3>Action Buttons</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Primary Button Text</label>
                  <input
                    value={form.primaryButtonText}
                    onChange={(e) => handleChange('primaryButtonText', e.target.value)}
                    placeholder="e.g. Go to Dashboard"
                  />
                </div>
                <div className="form-group">
                  <label>Primary Button Link</label>
                  <input
                    value={form.primaryButtonLink}
                    onChange={(e) => handleChange('primaryButtonLink', e.target.value)}
                    placeholder="e.g. /vendor-dashboard"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Secondary Button Text</label>
                  <input
                    value={form.secondaryButtonText}
                    onChange={(e) => handleChange('secondaryButtonText', e.target.value)}
                    placeholder="Optional second button"
                  />
                </div>
                <div className="form-group">
                  <label>Secondary Button Link</label>
                  <input
                    value={form.secondaryButtonLink}
                    onChange={(e) => handleChange('secondaryButtonLink', e.target.value)}
                    placeholder="e.g. /vendors"
                  />
                </div>
              </div>
            </div>

            <div className="form-actions">
              {editingId && (
                <button type="button" className="reset-btn" onClick={handleReset}>
                  Reset to Defaults
                </button>
              )}
              <button type="submit" className="save-btn" disabled={loading}>
                {loading ? 'Saving...' : 'Save Thank You Page'}
              </button>
            </div>
          </form>

          <div className="thankyou-preview-panel">
            <div className="preview-label">Live Preview</div>
            <div
              className="thankyou-preview"
              style={{
                background: `linear-gradient(135deg, ${form.backgroundColor} 0%, ${form.accentColor}22 100%)`,
                color: form.textColor,
              }}
            >
              {form.heroImage && (
                <div className="preview-hero">
                  <img src={resolveImageUrl(form.heroImage)} alt="" />
                </div>
              )}
              <div className="preview-icon-circle" style={{ background: form.accentColor }}>
                {renderPreviewIcon()}
              </div>
              <h2 className="preview-headline">{form.headline || 'Headline'}</h2>
              {form.subtitle && <p className="preview-subtitle">{form.subtitle}</p>}
              {form.description && (
                <div className="preview-description" dangerouslySetInnerHTML={{ __html: form.description }} />
              )}
              {form.extraContent && (
                <div className="preview-extra" dangerouslySetInnerHTML={{ __html: form.extraContent }} />
              )}
              <div className="preview-buttons">
                {form.primaryButtonText && (
                  <span className="preview-btn primary" style={{ background: form.accentColor }}>
                    {form.primaryButtonText}
                  </span>
                )}
                {form.secondaryButtonText && (
                  <span className="preview-btn secondary" style={{ borderColor: form.accentColor, color: form.accentColor }}>
                    {form.secondaryButtonText}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThankYouPageManagement;
